const { getByIds: getLocationsByIds } = require('./../location')
const { findById: findShipmentById } = require('./shipment-find')
const { parseCoordinates } = require('./../location/tools/coordinate-helper')
const { SHIPMENT_GPS_FAIL_REASONS } = require('./constants')
const distance = require('@turf/distance').default
const { point } = require('@turf/helpers')

const MAXIMUM_DELIVERY_RADIUS_KM = 2

// FE sends {lat,lng}; server stores {lat,long}. Accept either, normalize.
const normalizePosition = (p) => {
  if (!p) return null
  const lat = p.lat
  const long = p.long != null ? p.long : p.lng
  return (lat == null || long == null) ? null : { lat, long }
}

function calculateDistance (from, to) {
  return distance(point([from.long, from.lat]), point([to.long, to.lat]), { units: 'kilometers' })
}

async function resolveDestination (state, shipment, knownDestinationCoordinates) {
  const known = normalizePosition(knownDestinationCoordinates)
  if (known) return known

  const [location] = await getLocationsByIds(state, [shipment.destination.id])
  if (!location) {
    const error = new Error('Shipment destination not found.')
    error.status = 404
    throw error
  }

  const coords = normalizePosition(parseCoordinates(location.additionalData.latLng))
  if (!coords) {
    const error = new Error('Missing destination coordinates.')
    error.status = 404
    throw error
  }
  return coords
}

async function addGpsValidationFailComment (state, snapshotId, reason, position) {
  if (!Object.values(SHIPMENT_GPS_FAIL_REASONS).includes(reason)) {
    const error = new Error('Invalid gps validation fail reason')
    error.status = 400
    throw error
  }

  const driverPosition = normalizePosition(position)
  if (!driverPosition) {
    const error = new Error('Missing driver coordinates for GPS fail comment.')
    error.status = 400
    throw error
  }

  const snapshot = await state.shipmentsDb.get(snapshotId)
  const date = new Date().toJSON()
  snapshot.updatedAt = date
  snapshot.gpsValidationFailComment = { reason, date, position: driverPosition }
  await state.shipmentsDb.put(snapshot)

  return { _id: snapshotId, reason, date, position: driverPosition }
}

async function gpsValidation (state, shipmentId, currentCoordinates, knownDestinationCoordinates) {
  const driverPosition = normalizePosition(currentCoordinates)
  if (!driverPosition) {
    const error = new Error('Missing driver coordinates for GPS validation.')
    error.status = 400
    throw error
  }

  const shipment = await findShipmentById(state, shipmentId)
  if (shipment.isGpsAuthenticated) {
    const error = new Error('GPS validation already approved for current shipment.')
    error.status = 400
    throw error
  }

  const destinationCoordinates = await resolveDestination(state, shipment, knownDestinationCoordinates)
  const distanceKm = calculateDistance(driverPosition, destinationCoordinates)

  const snapshot = await state.shipmentsDb.get(shipment.snapshotId)
  const date = new Date().toJSON()
  snapshot.updatedAt = date

  if (distanceKm > MAXIMUM_DELIVERY_RADIUS_KM) {
    // Flagged: driver coords land on the follow-up gpsValidationFailComment,
    // so we deliberately don't also set gpsValidation here.
    snapshot.isGpsAuthenticated = false
    await state.shipmentsDb.put(snapshot)
    const error = new Error(`GPS validation failed, current distance from destination is ${distanceKm.toFixed(2)} km. Maximum distance allowed is ${MAXIMUM_DELIVERY_RADIUS_KM} km.`)
    error.status = 400
    throw error
  }

  snapshot.isGpsAuthenticated = true
  snapshot.gpsValidation = { position: driverPosition, date, distance: distanceKm }
  await state.shipmentsDb.put(snapshot)

  return {
    message: 'GPS validation succeeded',
    comment: `GPS validation succeeded, distance from destination was ${distanceKm.toFixed(2)} km.`
  }
}

module.exports = { calculateDistance, gpsValidation, addGpsValidationFailComment }
